import React from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileStickyBar from "../components/MobileStickyBar";
import Seo from "../components/Seo";
import BeforeAfter from "../components/BeforeAfter";
import { api, projectCover, COMPANY } from "../lib/site";

function resolveUrl(v) {
  if (!v) return "";
  if (typeof v === "string") {
    if (v.startsWith("http")) return v;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${v}`;
  }
  if (v.id) return `${process.env.REACT_APP_BACKEND_URL}/api/files/${v.id}`;
  return v.url || "";
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { data: project, isLoading, isError } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data, retry: false });

  if (isLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[#1B3626]" /></div>;
  if (isError || !project) return (
    <div className="min-h-screen"><Header /><div className="container-page section text-center"><h1 className="font-display text-3xl font-bold">Fant ikke prosjektet</h1><Link to="/prosjekter" className="btn-primary mt-6">Se alle prosjekter</Link></div><Footer /><MobileStickyBar /></div>
  );

  const pairs = (project.before_after || []).filter(p => p && (p.before_url || p.before_id) && (p.after_url || p.after_id));
  const galleryIds = project.image_ids || [];
  const galleryExt = project.external_images || [];

  return (
    <div className="min-h-screen">
      <Seo title={`${project.title} | NOUROZI EBYGG & ANLEGG`} description={project.description?.slice(0,160)} path={`/prosjekter/${id}`} />
      <Header />
      <section className="relative bg-[#181B18] text-white">
        {projectCover(project) && <img src={projectCover(project)} alt={project.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-page py-16 sm:py-20">
          <Link to="/prosjekter" className="inline-flex items-center gap-1.5 text-[#E8D6B6] font-semibold text-sm hover:text-white"><ArrowLeft size={14}/> Tilbake til prosjekter</Link>
          <div className="mt-4 text-xs tracking-widest uppercase text-[#E8D6B6]">{project.category} · {project.location}</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl">{project.title}</h1>
        </div>
      </section>

      <section className="section">
        <div className="container-page grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {project.description && <p className="text-[17px] leading-relaxed text-[#1C1F22] whitespace-pre-line">{project.description}</p>}

            {pairs.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-bold">Før og etter</h2>
                <div className="dot-divider mt-3" />
                <p className="text-sm text-[#555E68] mt-3">Dra i midten for å sammenligne før og etter.</p>
                <div className="mt-6 space-y-8">
                  {pairs.map((p, i) => (
                    <div key={i}>
                      <BeforeAfter beforeUrl={resolveUrl(p.before_id ? { id: p.before_id } : p.before_url)} afterUrl={resolveUrl(p.after_id ? { id: p.after_id } : p.after_url)} />
                      {p.caption && <div className="mt-2 text-sm text-[#555E68]">{p.caption}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(galleryIds.length > 0 || galleryExt.length > 0) && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-bold">Galleri</h2>
                <div className="dot-divider mt-3" />
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {galleryIds.map(gid => <img key={gid} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${gid}`} alt="" className="w-full aspect-square object-cover rounded-lg border border-[#E2DDD5]" loading="lazy" />)}
                  {galleryExt.map((u, i) => <img key={`x-${i}`} src={u} alt="" className="w-full aspect-square object-cover rounded-lg border border-[#E2DDD5]" loading="lazy" />)}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 bg-white border border-[#E2DDD5] rounded-2xl p-6">
              <h3 className="font-display text-xl font-bold">Vil du ha noe lignende?</h3>
              <p className="text-sm text-[#555E68] mt-1">Kontakt oss for en uforpliktende prat.</p>
              <Link to="/be-om-pristilbud" className="btn-primary w-full mt-4" data-testid="proj-quote-btn">Be om pristilbud</Link>
              <a href={`tel:${COMPANY.phoneClean}`} className="btn-secondary w-full mt-2" data-testid="proj-call-btn"><Phone size={14}/> Ring {COMPANY.phone}</a>
            </div>
          </aside>
        </div>
      </section>
      <Footer /><MobileStickyBar />
    </div>
  );
}
